package one.healthium.uscreen.ui.blocks

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Outline
import androidx.compose.ui.graphics.Paint
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawOutline
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Fill
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.drawIntoCanvas
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.toSize
import androidx.compose.ui.util.fastForEachIndexed
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import one.healthium.base.preferences.AppPreferences
import one.healthium.base.utils.kgToLb
import one.healthium.domain.user.models.Gender
import one.healthium.domain.user.models.PersonalPlanPeriod
import one.healthium.domain.user.models.PrognoseWeightModel
import one.healthium.domain.user.models.UserProfileModel
import one.healthium.domain.user.models.prognoseWeightArray
import kotlin.math.roundToInt
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_4
import one.healthium.ui.compose.theme.green_8
import one.healthium.ui.compose.theme.orange_4
import one.healthium.ui.compose.theme.red_5
import one.healthium.ui.compose.theme.white
import one.healthium.ui.compose.theme.yellow_7
import one.healthium.uscreen.domain.models.WeightGraphBlock
import one.healthium.uscreen.ui.UScreenAnswer
import one.healthium.base.utils.localizedName
import one.healthium.uscreen.ui.util.createShadowPaint
import one.healthium.uscreen.ui.util.drawShadow
import one.healthium.uscreen.ui.util.uscreenPadding
import org.koin.compose.koinInject
import kotlin.math.abs
import kotlin.time.Clock

// Chart layout constants
private val horizontalPadding = 25.dp
private val verticalStartPadding = 30.dp
private val verticalEndPadding = 30.dp

// Grid
private val verticalLineColor = Color(0xCCF2F2F2)
private val verticalLineWidth = 2.dp
private val legendSpacing = 8.dp

// Graph line
private val chartLineWidth = 4.dp
private val graphBrush = Brush.linearGradient(
    0f to red_5,
    0.34f to orange_4,
    0.73f to yellow_7,
    1f to green_8,
)

// Points
private val pointRadius = 6.dp
private val pointBorderWidth = 2.dp
private val lastPointRadius = 7.dp
private val lastPointBorderWidth = 14.dp
private val pointColors = listOf(
    red_5,
    orange_4,
    yellow_7,
    green_8,
)

// Tooltip
private val tooltipTriangleHeight = 14.dp
private val tooltipTriangleWidth = 18.dp
private val tooltipPadding = 6.dp

@Composable
fun UScreenWeightGraphBlock(
    modifier: Modifier,
    block: WeightGraphBlock,
    answers: Map<String, UScreenAnswer>,
) {
    val appPreferences = koinInject<AppPreferences>()
    val isMetric by appPreferences.isMetricFlow.collectAsState()

    val weight = (answers["weight"] as? UScreenAnswer.NumberAnswer)?.value
    val targetWeight = (answers["target_weight"] as? UScreenAnswer.NumberAnswer)?.value
    val height = (answers["height"] as? UScreenAnswer.NumberAnswer)?.value
    val age = (answers["age"] as? UScreenAnswer.NumberAnswer)?.value?.toInt()
    val genderKey = (answers["gender"] as? UScreenAnswer.ChoiceAnswer)?.selectedKeys?.firstOrNull()
    val gender = genderKey?.let { Gender.fromKey(it) }

    val period = remember(block.period) {
        when (block.period) {
            "3month" -> PersonalPlanPeriod.In3month
            "3month_short" -> PersonalPlanPeriod.In3monthShort
            "21days" -> PersonalPlanPeriod.In21days
            else -> PersonalPlanPeriod.In3month
        }
    }

    val userProfile = remember(weight, targetWeight, height, age, gender) {
        UserProfileModel(
            name = null,
            email = null,
            avatarUrl = null,
            age = age,
            weight = weight,
            targetWeight = targetWeight,
            height = height,
            gender = gender,
            additionalInfo = emptyMap(),
            additionalText = emptyMap(),
            statements = emptyMap(),
        )
    }

    val prognoseWeight = remember(userProfile, period) {
        userProfile.prognoseWeightArray(period)
    }

    if (prognoseWeight.isEmpty()) return

    WeightGraphChart(
        modifier = modifier
            .fillMaxWidth()
            .uscreenPadding(block.padding)
            .height(195.dp),
        prognoseWeight = prognoseWeight,
        isMetric = isMetric,
    )
}

private data class WeightGraphUiModel(
    val weightText: String,
    val periodText: String,
)

@Composable
private fun WeightGraphChart(
    modifier: Modifier = Modifier,
    prognoseWeight: List<PrognoseWeightModel>,
    isMetric: Boolean,
) {
    val textMeasurer = rememberTextMeasurer()

    val legendTextStyle = AppTheme.textStyles.body2
    val pointBorderColor = MaterialTheme.colorScheme.background
    val pointLegendTextStyle = AppTheme.textStyles.body1Bold
    val lastPointLegendTextStyle = AppTheme.textStyles.subTitleBold

    val prognoseWeightUi = remember(prognoseWeight, isMetric) {
        prognoseWeight.map { model ->
            val value = if (isMetric) model.weight.roundToInt() else model.weight.kgToLb().roundToInt()
            val unit = if (isMetric) "kg" else "lbs"
            WeightGraphUiModel(
                weightText = "$value $unit",
                periodText = toDayNames(model),
            )
        }
    }

    // Pre-compute TextMeasurer results
    val legendMeasurements = remember(prognoseWeightUi, legendTextStyle) {
        prognoseWeightUi.map { textMeasurer.measure(it.periodText, legendTextStyle) }
    }
    val pointWeightMeasurements = remember(prognoseWeightUi, pointLegendTextStyle) {
        prognoseWeightUi.dropLast(1).map { textMeasurer.measure(it.weightText, pointLegendTextStyle) }
    }
    val goalMeasurement = remember(prognoseWeightUi, lastPointLegendTextStyle) {
        prognoseWeightUi.lastOrNull()?.let {
            textMeasurer.measure(
                text = "Goal\n${it.weightText}",
                style = lastPointLegendTextStyle.copy(textAlign = TextAlign.Center),
            )
        }
    }

    val shadowPaint = remember { createShadowPaint(blurRadiusPx = 4f) }

    Canvas(modifier = modifier) {
        val points = drawGridAndCalculatePointsCoordinates(legendMeasurements)
        if (points.isEmpty()) return@Canvas

        drawPath(
            path = quadCurvedPathWithPoints(points),
            brush = graphBrush,
            style = Stroke(width = chartLineWidth.toPx()),
        )

        drawPoints(
            points = points,
            pointWeightMeasurements = pointWeightMeasurements,
            goalMeasurement = goalMeasurement,
            pointBorderColor = pointBorderColor,
            shadowPaint = shadowPaint,
        )
    }
}

private fun DrawScope.drawPoints(
    points: List<Offset>,
    pointWeightMeasurements: List<TextLayoutResult>,
    goalMeasurement: TextLayoutResult?,
    pointBorderColor: Color,
    shadowPaint: Paint,
) {
    points.fastForEachIndexed { index, point ->
        val pointColor = pointColors.getOrNull(index) ?: pointColors.last()

        if (index == points.size - 1 && goalMeasurement != null) {
            val borderRadius = lastPointBorderWidth.toPx() + 4.dp.toPx()

            val topLeftText = Offset(
                point.x - (goalMeasurement.size.width / 2),
                point.y - legendSpacing.toPx() - borderRadius - goalMeasurement.size.height - tooltipTriangleHeight.toPx(),
            )
            val topLeftTooltip = topLeftText.minus(Offset(tooltipPadding.toPx(), tooltipPadding.toPx()))
            val tooltipSize = Size(
                goalMeasurement.size.width + tooltipPadding.times(2).toPx(),
                goalMeasurement.size.height + tooltipPadding.times(2).toPx(),
            )
            drawRoundRect(
                color = pointColor,
                topLeft = topLeftTooltip,
                size = tooltipSize,
                cornerRadius = CornerRadius(tooltipPadding.toPx(), tooltipPadding.toPx()),
            )
            drawTriangle(
                offset = topLeftTooltip + Offset(
                    (tooltipSize.width - tooltipTriangleWidth.toPx()) / 2,
                    tooltipSize.height - 3.dp.toPx()
                ),
                size = Size(tooltipTriangleWidth.toPx(), tooltipTriangleHeight.toPx()),
                color = pointColor,
            )
            drawText(
                textLayoutResult = goalMeasurement,
                color = white,
                topLeft = topLeftText
            )

            drawCircleWithShadow(
                color = pointBorderColor,
                radius = borderRadius,
                center = point,
                shadowPaint = shadowPaint,
            )
            drawCircle(
                color = pointColor.copy(alpha = 0.2f),
                radius = lastPointBorderWidth.toPx(),
                center = point,
            )
            drawCircle(
                color = pointColor,
                radius = lastPointRadius.toPx(),
                center = point,
            )
        } else if (index < pointWeightMeasurements.size) {
            val borderRadius = pointRadius.toPx() + pointBorderWidth.toPx()
            val measuredText = pointWeightMeasurements[index]
            val topLeftText = Offset(
                point.x - (measuredText.size.width / 2),
                point.y - legendSpacing.toPx() - borderRadius - measuredText.size.height,
            )
            drawRect(
                color = pointBorderColor,
                topLeft = topLeftText,
                size = measuredText.size.toSize(),
            )
            drawText(
                textLayoutResult = measuredText,
                color = pointColor,
                topLeft = topLeftText
            )

            drawCircleWithShadow(
                color = pointBorderColor,
                radius = borderRadius,
                center = point,
                shadowPaint = shadowPaint,
            )
            drawCircle(
                color = pointColor,
                radius = pointRadius.toPx(),
                center = point,
            )
        }
    }
}

private fun DrawScope.drawGridAndCalculatePointsCoordinates(
    legendMeasurements: List<TextLayoutResult>,
): MutableList<Offset> {
    val count = legendMeasurements.size
    if (count <= 1) return mutableListOf()

    val width = size.width
    val height = size.height

    val points = mutableListOf<Offset>()

    val verticalLinesSpace = (width - (2 * horizontalPadding.toPx())) / (count - 1)
    var startOffset = horizontalPadding.toPx()
    val graphAvailableHeight = height - verticalStartPadding.toPx() - verticalEndPadding.toPx()
    val pointsStep = graphAvailableHeight / count

    legendMeasurements.fastForEachIndexed { index, measuredText ->
        points.add(Offset(startOffset, pointsStep * (index + 1)))

        drawText(
            textLayoutResult = measuredText,
            topLeft = Offset(
                startOffset - measuredText.size.width / 2,
                height - measuredText.size.height,
            ),
            color = gray_4,
        )

        drawLine(
            color = verticalLineColor,
            start = Offset(startOffset, 0f),
            end = Offset(
                startOffset,
                height - measuredText.size.height - legendSpacing.toPx(),
            ),
            strokeWidth = verticalLineWidth.toPx(),
        )

        startOffset += verticalLinesSpace
    }
    return points
}

private fun DrawScope.drawTriangle(offset: Offset, size: Size, color: Color) {
    val rect = Rect(offset, size)
    val trianglePath = Path().apply {
        moveTo(rect.topLeft.x, rect.topLeft.y)
        lineTo(rect.topRight.x, rect.topRight.y)
        lineTo(rect.bottomCenter.x, rect.bottomCenter.y)
        close()
    }

    drawIntoCanvas { canvas ->
        canvas.drawOutline(
            outline = Outline.Generic(trianglePath),
            paint = Paint().apply {
                this.color = color
                pathEffect = PathEffect.cornerPathEffect(rect.maxDimension / 3)
            }
        )
    }
}

private fun toDayNames(it: PrognoseWeightModel): String {
    if (it.inDays == 0) return "Today"
    val nowMs = Clock.System.now().toEpochMilliseconds()
    val futureMs = nowMs + it.inDays.toLong() * 86_400_000L
    val instant = kotlinx.datetime.Instant.fromEpochMilliseconds(futureMs)
    val dt = instant.toLocalDateTime(TimeZone.currentSystemDefault())
    return "${dt.month.localizedName(abbreviated = true)} ${dt.dayOfMonth}"
}

private fun quadCurvedPathWithPoints(points: List<Offset>): Path {
    val path = Path()

    if (points.isNotEmpty()) {
        path.moveTo(points.first().x, points.first().y)
    }

    if (points.size == 2) {
        path.lineTo(points.last().x, points.last().y)
    }

    var prevPoint = points.first()
    points.drop(1).forEach { point ->
        val midPoint = mid(prevPoint, point)

        val controlPoint1 = controlPointForPoints(midPoint, prevPoint)
        path.quadraticTo(
            x1 = controlPoint1.x,
            y1 = controlPoint1.y,
            x2 = midPoint.x,
            y2 = midPoint.y
        )

        val controlPoint2 = controlPointForPoints(midPoint, point)
        path.quadraticTo(
            x1 = controlPoint2.x,
            y1 = controlPoint2.y,
            x2 = point.x,
            y2 = point.y
        )

        prevPoint = point
    }

    return path
}

private fun mid(point1: Offset, point2: Offset): Offset {
    return Offset((point1.x + point2.x) / 2, (point1.y + point2.y) / 2)
}

private fun controlPointForPoints(p1: Offset, p2: Offset): Offset {
    val controlPoint = mid(p1, p2)
    val diffY = abs(p2.y - controlPoint.y)
    return if (p1.y < p2.y) {
        controlPoint.copy(y = controlPoint.y + diffY)
    } else if (p1.y > p2.y) {
        controlPoint.copy(y = controlPoint.y - diffY)
    } else {
        controlPoint
    }
}

private fun DrawScope.drawCircleWithShadow(
    center: Offset,
    radius: Float,
    color: Color,
    shadowPaint: Paint,
) {
    val path = Path().apply {
        addOval(Rect(center, radius))
    }
    drawShadow(path = path, paint = shadowPaint, offsetY = 1.dp)
    drawPath(
        path = path,
        color = color,
        style = Fill,
    )
}
